import { Component } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { DrawerComponent, DrawerMode, DrawerSelectEvent } from '@progress/kendo-angular-layout';
import { Role } from './models/role.model';
import { UserService } from './services/user.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html'
})

export class AppComponent {
    public selected = 'Dashboard';
    public items: Array<any>;
    public mode: DrawerMode = 'push';
    public mini = true;
    public expanded = true;
    public autoCollapse = true;
    public loggedIn = false;
    public drawer: DrawerComponent;
    public drawerWidth = 0;

    constructor(
        private router: Router,
        private authService: UserService) {
        // this.authService.getActiveUser();
    }

    ngOnInit() {
        // Update Drawer selected state when change router path
        this.router.events.subscribe((route: NavigationStart) => {
            if (route instanceof NavigationStart) {
                if (this.authService.currentUserValue) {
                    const userRole = this.authService.currentUserValue.role;
                    switch (userRole) {
                        case Role.Admin:
                            this.items = this.adminItems().map((item) => {
                                if (item.path && item.path === route.url) {
                                    item.selected = true;
                                    return item;
                                } else {
                                    item.selected = false;
                                    return item;
                                }
                            });
                            break;
                        case Role.Expert:
                            this.items = this.expertItems().map((item) => {
                                if (item.path && item.path === route.url) {
                                    item.selected = true;
                                    return item;
                                } else {
                                    item.selected = false;
                                    return item;
                                }
                            });
                            break;
                        case Role.Doctor:
                            this.items = this.doctorItems().map((item) => {
                                if (item.path && item.path === route.url) {
                                    item.selected = true;
                                    return item;
                                } else {
                                    item.selected = false;
                                    return item;
                                }
                            });
                            break;
                        case Role.Hospital:
                            this.items = this.hospitalItems().map((item) => {
                                if (item.path && item.path === route.url) {
                                    item.selected = true;
                                    return item;
                                } else {
                                    item.selected = false;
                                    return item;
                                }
                            });
                            break;
                        default:
                            break;
                    }
                }
            }
        });

        this.authService.isLoggedIn.subscribe(data => {
            if (data) {
                this.loggedIn = true;
                this.drawerWidth = 240;
            }
            else {
                this.loggedIn = false;
                this.drawerWidth = 0;
            }
        });

        this.setDrawerConfig();

        window.addEventListener('resize', () => {
            this.setDrawerConfig();
        });
    }

    ngOnDestroy() {
        window.removeEventListener('resize', () => { });
    }

    public setDrawerConfig() {
        const pageWidth = window.innerWidth;
        if (pageWidth <= 770) {
            this.mode = 'overlay';
            this.mini = false;
            this.expanded = false;
            this.autoCollapse = true;
        } else {
            this.mode = 'push';
            this.mini = true;
            this.expanded = true;
            this.autoCollapse = false;
        }
    }

    public adminItems() {
        return [
            { text: 'Mobile Dashboard', icon: 'k-i-aggregate-fields', path: '/admin/mobile-dashboard', selected: true },
            { text: 'Detailed Dashboard', icon: 'k-i-aggregate-fields', path: '/admin/detailed-dashboard', selected: false },
            { text: 'Urgent Appointments', icon: 'k-i-exception', path: '/admin/urgent', selected: false },
            { text: 'Unconfirmed Appointments', icon: 'k-i-calendar', path: '/admin/unconfirmed', selected: false },
            { text: 'Appointments', icon: 'k-i-calendar', path: '/admin/appointments', selected: false },
            { text: 'All Locations', icon: 'k-i-marker-pin', path: '/admin/hospitals', selected: false },
            { text: 'Organizations', icon: 'k-i-marker-pin', path: '/admin/organizations', selected: false },
            { text: 'Specialty Admin', icon: 'k-i-star-outline', path: '/admin/specialty', selected: false },
            { text: 'Experts', icon: 'k-i-star', path: '/admin/experts', selected: false },
            { text: 'Doctors', icon: 'k-i-user', path: '/admin/doctors', selected: false },
            { text: 'Patients', icon: 'k-i-user', path: '/admin/patients', selected: false },
            { text: 'Urgent Coverage', icon: 'k-i-check', path: '/admin/coverage', selected: false },
            { text: 'Notices', icon: 'k-i-file-txt', path: '/admin/notices', selected: false },
            { text: 'Accounting - Locations', icon: 'k-i-calculator', path: '/admin/accounting', selected: false },
            { text: 'Accounting - Experts', icon: 'k-i-calculator', path: '/admin/accounting-expert', selected: false },
            { text: 'Accounting - Doctors', icon: 'k-i-calculator', path: '/admin/accounting-doctor', selected: false },
            { separator: true },
            { text: 'Profile', icon: 'k-i-grid', path: '/admin/profile', selected: false },
            { text: 'Sign Out', icon: 'k-i-logout', path: '/login', selected: false },
        ];
    }

    public doctorItems() {
        return [
            { text: 'Dashboard', icon: 'k-i-aggregate-fields', path: '/doctor/dashboard', selected: true },
            // { text: 'Create Appointment', icon: 'k-i-plus-outline', path: '/doctor/appointment-create', selected: false },
            { text: 'All Appointments', icon: 'k-i-calendar', path: '/doctor/appointments', selected: false },
            { separator: true },
            { text: 'Profile', icon: 'k-i-grid', path: '/doctor/profile', selected: false },
            { text: 'Sign Out', icon: 'k-i-logout', path: '/login', selected: false },
        ];
    }

    public expertItems() {
        return [
            { text: 'Dashboard', icon: 'k-i-aggregate-fields', path: '/expert/dashboard', selected: true },
            { text: 'Appointment Requests', icon: 'k-i-calendar', path: '/expert/appointment-request', selected: false },
            { text: 'Open Consults', icon: 'k-i-folder-open', path: '/expert/open-consult', selected: false },
            { text: 'Availability', icon: 'k-i-grid', path: '/expert/availability', selected: false },
            { text: 'Accounting', icon: 'k-i-calculator', path: '/expert/accounting', selected: false },
            { separator: true },
            { text: 'Profile', icon: 'k-i-grid', path: '/expert/profile', selected: false },
            { text: 'Sign Out', icon: 'k-i-logout', path: '/login', selected: false },
        ];
    }

    public hospitalItems() {
        return [
            { text: 'Dashboard', icon: 'k-i-aggregate-fields', path: '/hospital/dashboard', selected: true },
            { text: 'Doctors', icon: 'k-i-user', path: '/hospital/doctors', selected: false },
            { text: 'Accounting', icon: 'k-i-calculator', path: '/hospital/accounting', selected: false },
            { separator: true },
            { text: 'Profile', icon: 'k-i-grid', path: '/hospital/profile', selected: false },
            { text: 'Sign Out', icon: 'k-i-logout', path: '/login', selected: false },
        ];
    }

    public toggleDrawer(drawer: DrawerComponent): void {
        drawer.toggle();
    }

    public onSelect(ev: DrawerSelectEvent): void {
        if (ev.item.text != null && ev.item.text != '') {
            if (ev.item.path === '/login') {
                this.authService.logout();
            } else {
                this.router.navigate([ev.item.path]);
                this.selected = ev.item.text;
            }
        }
    }
}
